import { useEffect, useState } from "react";

import classNames from "classnames/bind";
import styles from "../styles/scheduling.module.scss";

import { Title } from "../shared/components/Title";
import { Empty } from "../shared/components/Empty";
import { Icon } from "../shared/components/core/Icon";
import { Button } from "../shared/components/Button";
import { Checkbox } from "../shared/components/Checkbox";
import { Select } from "../shared/components/Select";
import { NumberInput } from "../shared/components/NumberInput";
import { Loading } from "../shared/components/Loading";
import { EnvironmentStorage } from "../shared/tools/EnvironmentStorage";
import { EnvironmentsAPI } from "../providers/EnvironmentsAPI";
import { Drawer } from "../shared/components/Drawer";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { If } from "shared/components/control-statements";
import { UserStorage } from "shared/tools/UserStorage";

dayjs.extend(duration);

const css = classNames.bind(styles);

const DaysWeek = [
  {
    key: 0,
    short: "D",
    full: "domingo",
  },
  {
    key: 1,
    short: "S",
    full: "segunda-feira",
  },
  {
    key: 2,
    short: "T",
    full: "terça-feira",
  },
  {
    key: 3,
    short: "Q",
    full: "quarta-feira",
  },
  {
    key: 4,
    short: "Q",
    full: "quinta-feira",
  },
  {
    key: 5,
    short: "S",
    full: "sexta-feira",
  },
  {
    key: 6,
    short: "S",
    full: "sábado",
  },
];

export default function Scheduling() {
  const [visible, setVisible] = useState();
  const [collapsed, setCollapsed] = useState(false);
  const [openDeactivate, setOpenDeactivate] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [timesActive, setTimesActive] = useState(true);
  const [maxPeriod, setMaxPeriod] = useState("hour");
  const [minPeriod, setMinPeriod] = useState("hour");
  const [isFirstConfiguration, setIsFirstConfiguration] = useState(true);
  const [minTime, setMinTime] = useState({ unit: "hour", value: "" });
  const [maxTime, setMaxTime] = useState({ unit: "hour", value: "" });
  const [weekDays, setWeekDays] = useState([]);
  const [freeDelivery, setFreeDelivery] = useState({});
  const [integration, setIntegration] = useState({});
  const [enable, setEnable] = useState(true);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const environment = EnvironmentStorage.getEnvironment();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const environmentId = EnvironmentStorage.getEnvironment().id;
      const response = await EnvironmentsAPI.getConfigurationsSchedule(
        +environmentId
      );

      if (response._code === 200) {
        setVisible(true);
        setIsFirstConfiguration(false);

        const data = response.getData();

        const active =
          data.availability.active !== undefined
            ? data.availability.active
            : true;

        setMaxTime(data.maximum);
        setMinTime(data.minimum);
        setMaxPeriod(data.maximum.unit);
        setMinPeriod(data.minimum.unit);
        setSelectedTimes(data.availability.periods);
        setTimesActive(active);
        setWeekDays(data.weekDays || []);
        setFreeDelivery(data.freeDelivery || {});
        setIntegration(data.integration || {});
        setEnable(data.active);
      } else {
        setVisible(false);
        setIsFirstConfiguration(true);
        setEnable(false);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const mountPayload = () => {
    return {
      availability: {
        periods: selectedTimes,
        active: timesActive,
      },
      active: enable,
      minimum: minTime,
      maximum: maxTime,
      weekDays: weekDays,
      freeDelivery: freeDelivery,
      integration: integration,
    };
  };

  const update = async () => {
    setSaving(true);
    const environemtId = EnvironmentStorage.getEnvironment().id;
    const response = await EnvironmentsAPI.putConfigurationsSchedule(
      +environemtId,
      {
        schedule: mountPayload(),
      }
    );
    if (response.getErrors([]).length <= 0) {
      const data = response.getData({
        schedule: { availability: { periods: [] } },
      });
      setSelectedTimes(data.schedule.availability.periods || []);
      setVisible(true);
      setIsFirstConfiguration(false);
    }
    setSuccess(true);
    setSaving(false);
  };

  const isValid = () => {
    const hasSelectedTime =
      (selectedTimes && selectedTimes.length > 0) || !timesActive;
    const hasMinTime =
      minTime && minTime.value && minTime.value > 0 && minTime.unit;
    const hasMaxTime =
      maxTime && maxTime.value && maxTime.value > 0 && maxTime.unit;
    const hasWeekDays = weekDays && weekDays.length > 0;

    const convertToMilliseconds = (unit, value) => {
      return dayjs.duration({ [unit]: value }).asMilliseconds();
    };
    const minIsLessThanMax = () => {
      const minInMilliseconds = convertToMilliseconds(
        minTime.unit,
        parseFloat(minTime.value)
      );
      const maxInMilliseconds = convertToMilliseconds(
        maxTime.unit,
        parseFloat(maxTime.value)
      );

      return minInMilliseconds <= maxInMilliseconds;
    };

    return (
      hasSelectedTime &&
      hasMinTime &&
      hasMaxTime &&
      minIsLessThanMax() &&
      hasWeekDays
    );
  };

  const getSubtitle = () => {
    if (environment?.brand)
      return (
        `${environment.brand.name} - ${environment.name}` +
        (environment.active === false ? " (Inativa)" : "")
      );
    return environment?.name;
  };

  const isSelectedDay = (day) => {
    if (!Array.isArray(selectedTimes)) {
      console.error("selectedTimes is not an array:", selectedTimes);
      return false;
    }
    let startTime = day.split(" - ")[0];
    return selectedTimes.includes(startTime);
  };

  const selectDaysWeek = (day) => {
    let startTime = day.split(" - ")[0];
    let tempArr = [...selectedTimes];
    const index = tempArr.indexOf(startTime);

    if (index > -1) {
      tempArr = tempArr.filter((item) => item !== startTime);
    } else {
      tempArr.push(startTime);
    }

    setSelectedTimes(tempArr);
  };

  const getTimeRange = (begin = "00:00", end = "23:00", label = false) => {
    const timeRange = generateTimeRange(label);
    const indexOfBegin = timeRange.indexOf(begin);
    const indexOfEnd = label
      ? timeRange.map((time) => time.substr(0, 5)).indexOf(end)
      : timeRange.indexOf(end);

    const validTimeRange = timeRange.slice(
      indexOfBegin !== -1 ? indexOfBegin : 0,
      indexOfEnd !== -1 ? indexOfEnd + 1 : timeRange.length - 1
    );

    return validTimeRange;
  };

  const generateTimeRange = () => {
    const timeHourOptions = [];
    for (let hours = 6; hours < 24; hours++) {
      for (let minutes = 0; minutes < 1; minutes++) {
        timeHourOptions.push(
          `${`${hours}`.padStart(2, "0")}:${`${minutes * 60}`.padStart(
            2,
            "0"
          )} - ${`${hours === 23 ? "00" : hours + 1}`.padStart(2, "0")}:${`${
            minutes * 60
          }`.padStart(2, "0")}`
        );
      }
    }

    for (let hours = 0; hours <= 6; hours++) {
      for (let minutes = 0; minutes < 1; minutes++) {
        timeHourOptions.push(
          `${`${hours}`.padStart(2, "0")}:${`${minutes * 60}`.padStart(
            2,
            "0"
          )} - ${`${hours + 1}`.padStart(2, "0")}:${`${minutes * 60}`.padStart(
            2,
            "0"
          )}`
        );
      }
    }

    return timeHourOptions;
  };

  const getTimes = () => {
    const times = getTimeRange();

    return times.map((time) => {
      return {
        value: time,
        label: time,
      };
    });
  };

  const handleMinTimeUnitChange = (e) => {
    const selectedValue = e.target.value;
    setMinPeriod(selectedValue);
    setMinTime({ unit: selectedValue, value: "" });
  };

  const handleMaxTimeUnitChange = (e) => {
    const selectedValue = e.target.value;
    setMaxPeriod(selectedValue);
    setMaxTime({ unit: selectedValue, value: "" });
  };

  const handleMinTimeValueChange = (e) => {
    const value = e.target ? e.target.value : e;
    const updatedMinTime = { ...minTime, value };
    setMinTime(updatedMinTime);
  };

  const handleMaxTimeValueChange = (e) => {
    const value = e.target ? e.target.value : e;

    const updatedMaxTime = { ...maxTime, value };
    setMaxTime(updatedMaxTime);
  };

  const handleSelectWeekDay = (day) => {
    let newWeekDays = [];
    if (weekDays.includes(day)) {
      newWeekDays = weekDays.filter((d) => d !== day);
    } else {
      newWeekDays = [...weekDays, day];
    }

    setWeekDays(newWeekDays);
  };

  const handleFreeDeliveryChange = () => {
    setFreeDelivery({
      ...(freeDelivery || {}),
      allowSameDay: !freeDelivery?.allowSameDay,
    });
  };

  if (loading) {
    return <Loading fullContent={true} />;
  }

  const valid = isValid();

  return (
    <div className={styles.schedulingContainer}>
      <Title
        title="Agendamento"
        subtitle={getSubtitle()}
        backTo={{ url: "/configuracoes", name: "Configurações" }}
      >
        {visible &&
          !isFirstConfiguration &&
          (enable ? (
            <Button design="default" onClick={() => setOpenDeactivate(true)}>
              Desativar
            </Button>
          ) : (
            <Button
              design="default"
              onClick={() => {
                setEnable(true);
                setOpenDeactivate(false);
              }}
            >
              Ativar
            </Button>
          ))}
      </Title>
      {!visible && (
        <Empty>
          <Icon name="calendar" light size="4x" />
          <p>O agendamento está desativado.</p>
          <span>
            Através desta ferramenta, você pode disponibilizar agendamentos para
            os seus clientes.
          </span>
          <Button design="primary" onClick={() => setVisible(true)}>
            Ativar agendamento
          </Button>
        </Empty>
      )}
      {visible && (
        <>
          <div className={styles.group}>
            <div
              className={`${styles.header} ${
                !collapsed ? styles.collapsed : ""
              }`}
              onClick={() => setCollapsed(!collapsed)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <Icon name="clock" />
                </div>
                <p className={styles.title}>Configuração de faixa de horário</p>
              </div>
              <div className={styles.chevron}>
                <Icon name="chevron-down" />
              </div>
            </div>

            {collapsed && (
              <div className={styles.content}>
                <div className={styles.horaries}>
                  {getTimes().map((time) => {
                    return (
                      <Checkbox
                        className="mb-10"
                        name={time.label}
                        checked={isSelectedDay(time.value)}
                        disabled={!timesActive}
                        onChange={() => selectDaysWeek(time.value)}
                        key={time.value}
                      >
                        {time.label}
                      </Checkbox>
                    );
                  })}
                </div>
                <div className={styles.horaries}>
                  <Checkbox
                    className="mb-10"
                    name="Não considerar horários"
                    checked={!timesActive}
                    onChange={() => {
                      setTimesActive(!timesActive);
                    }}
                  >
                    Não considerar horários
                  </Checkbox>
                </div>
              </div>
            )}
          </div>

          <div className={styles.group}>
            <div className={styles.header}>
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <Icon name="calendar" light />
                </div>
                <p className={styles.title}>
                  Configuração de tempo mínimo e máximo
                </p>
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.times}>
                <NumberInput
                  label="Tempo mínimo para agendar"
                  design="three"
                  value={minTime.value}
                  onChange={handleMinTimeValueChange}
                />

                <Select
                  label="Selecione a medida"
                  options={[
                    {
                      label: minTime.value === "1" ? "Hora" : "Horas",
                      value: "hours",
                    },
                    {
                      label: minTime.value === "1" ? "Dia" : "Dias",
                      value: "days",
                    },
                  ]}
                  value={minPeriod}
                  onChange={handleMinTimeUnitChange}
                  design="three"
                />
                <div className={styles.hairline}>
                  <NumberInput
                    label="Tempo máximo para agendar"
                    design="three"
                    value={maxTime.value}
                    onChange={handleMaxTimeValueChange}
                  />
                </div>
                <Select
                  label="Selecione a medida"
                  options={[
                    {
                      label: maxTime.value === "1" ? "Hora" : "Horas",
                      value: "hours",
                    },
                    {
                      label: maxTime.value === "1" ? "Dia" : "Dias",
                      value: "days",
                    },
                  ]}
                  value={maxPeriod}
                  onChange={handleMaxTimeUnitChange}
                  design="three"
                />
              </div>
            </div>
          </div>

          <div className={styles.group}>
            <div className={styles.header}>
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <Icon name="calendar" light />
                </div>
                <p className={styles.title}>Configuração de dias da semana</p>
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.days}>
                {DaysWeek.map((day) => (
                  <div
                    className={css({
                      day: true,
                      isSelected: weekDays.includes(day.key),
                    })}
                    onClick={() => handleSelectWeekDay(day.key)}
                    key={day.full}
                  >
                    {day.short}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <If cond={(environment?.holdingId == 1069 || environment?.holdingId == 2232) && UserStorage.isGodMode}>
            <div className={styles.group}>
              <div className={styles.header}>
                <div className="d-flex align-center">
                  <div className={styles.icon}>
                    <Icon name="map" />
                  </div>
                  <p className={styles.title}>Configuração de frete grátis</p>
                </div>
              </div>

              <div className={styles.content}>
                <div className="d-flex flex-column mt-20">
                  <Checkbox
                    className="mb-10"
                    name="Permitir no mesmo dia"
                    checked={!freeDelivery?.allowSameDay}
                    onChange={() => handleFreeDeliveryChange()}
                  >
                    Não fornecer frete grátis para pedidos no mesmo dia
                  </Checkbox>
                </div>
              </div>
            </div>

            <div className={styles.group}>
              <div className={styles.header}>
                <div className="d-flex align-center">
                  <div className={styles.icon}>
                    <Icon name="fa-sliders" />
                  </div>
                  <p className={styles.title}>Configuração da integração com o PDV</p>
                </div>
              </div>

              <div className={styles.content}>
                <div className="d-flex flex-column mt-20">
                  <Checkbox
                    className="mb-10"
                    name="Enviar somente quando a loja estiver aberta"
                    checked={!integration?.allowSendWhenStoreIsClosed}
                    onChange={() => setIntegration({
                      ...integration,
                      allowSendWhenStoreIsClosed: !integration?.allowSendWhenStoreIsClosed
                    })}
                  >
                    Enviar somente quando a loja estiver aberta
                  </Checkbox>
                </div>
              </div>
            </div>

          </If>
        </>
      )}

      {visible && (
        <div className={styles.save}>
          <Button
            design="primary"
            className="w-100"
            loading={saving}
            disabled={!valid || saving}
            onClick={() => update()}
          >
            Salvar
          </Button>
        </div>
      )}
      <Drawer
        visible={success}
        onClose={() => {
          setIsFirstConfiguration(false);
        }}
        direction="top-alert"
      >
        <div className={styles.deactivate}>
          <p className={styles.title}>Sucesso</p>
          <span className={styles.subtitle}>
            Configurações Salvas com sucesso
          </span>
          <div className="d-flex align-center w-100">
            <Button
              design="primary"
              onClick={() => setSuccess(false)}
              className="w-100 mr-15"
            >
              Ok
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        visible={openDeactivate}
        onClose={() => setOpenDeactivate(true)}
        direction="top-alert"
      >
        <div className={styles.deactivate}>
          <p className={styles.title}>Atenção</p>
          <span className={styles.subtitle}>
            Deseja desativar a disponibilidade de agendamento para os seus
            clientes?
          </span>
          <div className="d-flex align-center w-100">
            <Button
              design="primary"
              onClick={() => setOpenDeactivate(false)}
              className="w-100 mr-15"
            >
              Cancelar
            </Button>
            <Button
              design="default"
              onClick={() => {
                setEnable(false);
                setOpenDeactivate(false);
              }}
              className="w-100"
            >
              Desativar
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
